import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, ChevronLeft, ChevronRight, ChevronRight as Arrow, Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import { OccupationCalendar, fmtF } from '../components/occupation/OccupationCalendar';

const API = process.env.REACT_APP_BACKEND_URL;
const FILTERS = [['all', 'Tous'], ['led', 'LED'], ['deroulant', 'Déroulants'], ['grand', 'Grand Format'], ['poteau', 'Poteau']];
const VIEWS = [['semaine', 'Semaine', 7], ['mois', 'Mois', 31], ['trim', 'Trim.', 92]];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const card = 'bg-white rounded-2xl border border-slate-200/70 shadow-[0_2px_12px_rgba(15,23,42,0.05)]';
const BAR = { led: '#3730a3', deroulant: '#f59e0b', grand: '#16a34a', poteau: '#8b5cf6' };

function Kpi({ label, value, sub, color, ring, testid }) {
  return (
    <div className={`${card} relative p-5 pl-6 overflow-hidden`} data-testid={testid}>
      <span className="absolute left-0 top-4 bottom-4 w-1.5 rounded-r" style={{ background: color }} />
      <p className="text-sm text-slate-500">{label}</p>
      <div className="flex items-center justify-between">
        <p className="text-3xl font-extrabold text-slate-900 mt-1 tracking-tight">{value}</p>
        {ring != null && (
          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90"><circle cx="18" cy="18" r="14" fill="none" stroke="#e2e8f0" strokeWidth="4" />
            <circle cx="18" cy="18" r="14" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={`${(ring / 100) * 88} 88`} /></svg>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-1">{sub}</p>
    </div>
  );
}

export default function Occupation({ user, onLogout }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('mois');
  const [start, setStart] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [data, setData] = useState(null);

  const days = view === 'mois' ? new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate() : VIEWS.find((v) => v[0] === view)[2];
  useEffect(() => {
    axios.get(`${API}/api/occupation`, { params: { start: iso(start), days } })
      .then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement de l'occupation"));
  }, [start, days]);

  const shift = (dir) => setStart((s) => { const d = new Date(s); if (view === 'semaine') d.setDate(d.getDate() + 7 * dir); else d.setMonth(d.getMonth() + (view === 'trim' ? 3 : 1) * dir); return d; });
  const changeView = (v) => { setView(v); setStart((s) => { const d = new Date(s); if (v === 'semaine') d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); else d.setDate(1); return d; }); };

  const newContrat = (state = {}) => navigate('/contrats', { state: { openNewModal: true, ...state } });
  const onFree = (f, s, e) => {
    const d0 = new Date(`${data.start}T00:00:00`); d0.setDate(d0.getDate() + s);
    const d1 = new Date(`${data.start}T00:00:00`); d1.setDate(d1.getDate() + e + 1);
    const prefillType = { led: 'LED Digital', deroulant: 'Déroulant', grand: 'Grand Format', poteau: 'Poteau' }[f.type];
    newContrat({ prefillEmplacement: f.emplacement, prefillType, prefillDebut: iso(d0), prefillFin: iso(d1) });
  };
  const onRenew = (b) => newContrat({ prefillEntreprise: b.client });
  const onView = (b) => navigate(`/contrats?contrat=${b.contrat_id || ''}`);

  if (!data) return <Layout user={user} onLogout={onLogout} currentPage="occupation"><div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div></Layout>;
  const shown = filter === 'all' ? data : { ...data, groups: data.groups.filter((g) => g.type === filter), faces: data.faces.filter((f) => f.type === filter) };
  const k = data.kpis;
  const label = view === 'mois' ? start.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : `${new Date(`${data.start}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${new Date(`${data.end}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <Layout user={user} onLogout={onLogout} currentPage="occupation">
      <div className="space-y-5" data-testid="occupation-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-slate-500">Opérations › <span className="text-blue-600 font-semibold">Occupation &amp; Réservations</span></p>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-1">Occupation des panneaux</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v}`}
                className={`h-10 px-5 rounded-full text-sm font-semibold border transition-colors ${filter === v ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300'}`}>{l}</button>
            ))}
            <button onClick={() => newContrat()} className="h-10 px-5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold inline-flex items-center gap-2 transition-colors" data-testid="btn-nouvelle-reservation">
              <Plus className="h-4 w-4" /> Nouvelle réservation
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          <Kpi label="Taux d'occupation" value={`${k.taux} %`} sub={`${k.nb_faces} faces · ${data.days} jours`} color="#2563eb" ring={k.taux} testid="kpi-taux-occ" />
          <Kpi label={`Revenu réel (${data.days} j)`} value={fmtF(k.revenu)} sub={`sur ${fmtF(k.potentiel)} FCFA potentiels`} color="#16a34a" testid="kpi-revenu" />
          <Kpi label="Faces libres aujourd'hui" value={`${k.libres_aujourdhui} / ${k.nb_faces}`} sub="prêtes à être vendues" color="#f59e0b" testid="kpi-libres" />
          <Kpi label="Contrats à renouveler" value={k.a_renouveler} sub="expirent sous 14 jours" color="#dc2626" testid="kpi-renouveler" />
          <Kpi label="Manque à gagner" value={fmtF(k.manque)} sub="FCFA sur la période" color="#7c3aed" testid="kpi-manque" />
        </div>

        <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_360px] gap-5">
          <div className={`${card} p-5 min-w-0`}>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <h2 className="text-lg font-bold text-slate-900">Calendrier d'occupation</h2>
              <div className="flex items-center gap-3">
                <div className="flex bg-slate-100 rounded-full p-0.5">
                  {VIEWS.map(([v, l]) => (
                    <button key={v} onClick={() => changeView(v)} data-testid={`view-${v}`}
                      className={`px-3 h-8 rounded-full text-sm font-semibold transition-colors ${view === v ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'}`}>{l}</button>
                  ))}
                </div>
                <div className="flex items-center bg-slate-100 rounded-full">
                  <button onClick={() => shift(-1)} className="w-8 h-8 flex items-center justify-center" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm font-bold text-slate-900 min-w-[110px] text-center capitalize" data-testid="cal-label">{label}</span>
                  <button onClick={() => shift(1)} className="w-8 h-8 flex items-center justify-center" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
            <OccupationCalendar data={shown} onFree={onFree} onRenew={onRenew} onView={onView} />
            <div className="flex items-center gap-5 flex-wrap mt-4 text-xs text-slate-600">
              <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-blue-600" />Loué</span>
              <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-slate-200" />Libre</span>
              <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded border-2 border-amber-400" />À renouveler</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500" />Payé</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500" />Partiel</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-rose-500" />Non payé</span>
              <span className="ml-auto">Cliquez un créneau libre pour réserver</span>
            </div>
          </div>

          <div className={`${card} p-5`} data-testid="a-traiter">
            <div className="flex items-center justify-between"><h2 className="text-lg font-bold text-slate-900">À traiter</h2>
              <span className="text-xs font-bold bg-rose-50 text-rose-600 rounded-full px-2.5 py-0.5">{data.a_renouveler.length + data.creneaux_libres.length}</span></div>
            <p className="text-xs font-bold uppercase text-rose-600 mt-4 mb-2">Contrats à renouveler</p>
            {data.a_renouveler.length === 0 && <p className="text-sm text-slate-500 mb-2">Aucun contrat n'expire sous 14 jours.</p>}
            {data.a_renouveler.map((r, i) => (
              <div key={i} className="rounded-xl border border-amber-200 bg-amber-50/60 border-l-4 border-l-amber-400 p-3 mb-2.5">
                <p className="font-bold text-slate-900">{r.code} · {r.client}</p>
                <div className="flex items-center justify-between mt-1"><span className="text-sm text-amber-700">Expire dans {r.expire_dans} jours</span>
                  <button onClick={() => onRenew(r)} data-testid={`renew-${r.code}`} className="h-7 px-3 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition-colors">Renouveler</button></div>
              </div>
            ))}
            <p className="text-xs font-bold uppercase text-violet-600 mt-4 mb-2">Créneaux libres · manque à gagner</p>
            {data.creneaux_libres.map((c, i) => (
              <div key={i} className="rounded-xl border border-slate-200 border-l-4 border-l-violet-500 p-3 mb-2.5" data-testid={`libre-${c.code}`}>
                <p className="font-bold text-slate-900">{c.code} · {c.jours} jours libres</p>
                <p className="text-sm font-bold text-violet-700">≈ {fmtF(c.manque)} FCFA perdus</p>
                <button onClick={() => navigate('/prospects')} data-testid={`propose-${c.code}`} className="mt-2 h-7 px-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors">Proposer à un prospect</button>
              </div>
            ))}
          </div>
        </div>

        <div className={`${card} p-5 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr_auto] gap-6 items-center`} data-testid="occupation-summary">
          <div>
            <p className="font-bold text-slate-900">Revenu réel vs potentiel · {data.days} jours</p>
            <p className="mt-1"><span className="text-2xl font-extrabold text-emerald-600">{fmtF(k.revenu)} FCFA</span> <span className="text-sm text-slate-500 ml-2">sur {fmtF(k.potentiel)} potentiels</span></p>
            <div className="h-2 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${k.potentiel ? (100 * k.revenu) / k.potentiel : 0}%` }} /></div>
          </div>
          <div>
            <p className="font-bold text-slate-900 mb-2">Occupation par type</p>
            <div className="grid grid-cols-4 gap-4">
              {data.groups.map((g) => (
                <div key={g.ligne}><div className="flex justify-between text-sm"><span className="text-slate-600">{g.label}</span><b>{g.occupe_pct} %</b></div>
                  <div className="h-2 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${g.occupe_pct}%`, background: BAR[g.type] }} /></div></div>
              ))}
            </div>
          </div>
          <button onClick={() => navigate('/prospects')} className="h-11 px-6 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold inline-flex items-center gap-2 transition-colors" data-testid="btn-prospects-a-placer">
            Voir les prospects à placer <Arrow className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-slate-400">Calculé à partir de vos contrats et des {k.nb_faces} faces de Nos Panneaux (tarif de référence par affiche et par jour).</p>
      </div>
    </Layout>
  );
}
